import React from "react";
import {
  Box,
  CircularProgress,
  Stack,
  SvgIconProps,
  Tooltip,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearch";
import EditIcon from "@mui/icons-material/Edit";
import { ContextMenu } from "../ContextMenu/ContextMenu";
import {
  BorderAllOutlined,
  BorderClearOutlined,
  BorderColorOutlined,
  ContentCopyOutlined,
  DeleteOutlineOutlined,
  Download,
  EditAttributesRounded,
  FeaturedPlayListOutlined,
  PreviewOutlined,
  PublishedWithChanges,
  SortOutlined,
  Visibility,
} from "@mui/icons-material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ChecklistIcon from "@mui/icons-material/Checklist";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PreviewIcon from "@mui/icons-material/Preview";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";

import { Dialog } from "../Dialogs/Dialog";
import HistoryIcon from "@mui/icons-material/History";
import CommentIcon from "@mui/icons-material/Comment";

export interface IMenuOption {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}
export interface IContextMenuProps {
  menuOptions: IMenuOption[];
}

interface IStandardTableActionsProps {
  status?: string;
  onDeleteClick?: () => void;
  onReactiveClick?: () => void;
  commentBoxTypeId?: string | number;
  onEditClick?: () => void;
  onViewClick?: () => void;
  disableEdit?: boolean;
  onViewHistoryClick?: () => void;
  onCommentClick?: () => void;
  onDownloadPreview?: () => void;
  onDownLoadClick?: () => void;
  onDuplicateClick?: () => void;
  onViewDetails?: () => void;
  downloadLoading?: boolean;
  otherActions?: {
    icon: React.ComponentType<SvgIconProps>;
    onClick: () => void;
    tooltip: string;
  }[];
  more?: {
    menuItems: IContextMenuProps["menuOptions"];
  };
}

export const StandardTableActions: React.FC<IStandardTableActionsProps> = (
  props,
) => {
  const {
    status,
    onDeleteClick,
    onReactiveClick,
    onViewClick,
    onEditClick,
    onViewHistoryClick,
    onCommentClick,
    onDownLoadClick,
    onDownloadPreview,
    onDuplicateClick,
    onViewDetails,
    downloadLoading,
    more,
    commentBoxTypeId,
    otherActions,
    disableEdit,
  } = props;

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"flex-start"}
        sx={{
          svg: {
            cursor: "pointer",
          },
        }}
        spacing={1}
      >
        {onReactiveClick &&
          status &&
          ["CANCELLED", "INACTIVE", "ARCHIVE"].includes(status) && (
            <Box onClick={() => setOpenDeleteModal(true)}>
              <Tooltip title="Reactive">
                <PublishedWithChanges
                  sx={{ cursor: "pointer" }}
                  fontSize="small"
                  color="error"
                />
              </Tooltip>
            </Box>
          )}

        {onEditClick && (
          <Box onClick={() => (disableEdit ? null : onEditClick())}>
            <Tooltip title="Edit">
              <BorderColorOutlined
                color={disableEdit ? "disabled" : "primary"}
              />
            </Tooltip>
          </Box>
        )}
        {onViewClick && (
          <Box onClick={onViewClick}>
            <Tooltip title="Preview">
              <PreviewOutlined color="primary" />
            </Tooltip>
          </Box>
        )}
        {onDuplicateClick && (
          <Box onClick={onDuplicateClick}>
            <Tooltip title="Duplicate">
              <ContentCopyOutlined color="primary" />
            </Tooltip>
          </Box>
        )}
        {onViewHistoryClick && (
          <Box onClick={onViewHistoryClick}>
            <Tooltip title="Activity">
              <HistoryIcon color="primary" />
            </Tooltip>
          </Box>
        )}
        {onCommentClick && (
          <Box onClick={onCommentClick}>
            <Tooltip title="Activity">
              <AssignmentTurnedInIcon color="primary" fontSize="medium" />
            </Tooltip>
          </Box>
        )}
        {onDownloadPreview && (
          <Box onClick={onDownloadPreview}>
            <Tooltip title="Preview">
              <ContentPasteSearchOutlinedIcon color="primary" />
            </Tooltip>
          </Box>
        )}
        {onViewDetails && (
          <Box onClick={onViewDetails}>
            <Tooltip title="View Details">
              <Visibility color="primary" />
            </Tooltip>
          </Box>
        )}
        {onDeleteClick &&
          status &&
          !["CANCELLED", "INACTIVE", "ARCHIVE"].includes(status) && (
            <Box onClick={() => setOpenDeleteModal(true)}>
              <Tooltip title="Delete">
                <DeleteForeverIcon
                  sx={{ cursor: "pointer" }}
                  fontSize="medium"
                  color="primary"
                />
              </Tooltip>
            </Box>
          )}
        {onDownLoadClick && (
          <Box onClick={!downloadLoading ? onDownLoadClick : undefined}>
            <Tooltip title="Download">
              <Download color="primary" />
            </Tooltip>
          </Box>
        )}
        {otherActions &&
          otherActions.map((action, index) => (
            <Box key={index} onClick={action.onClick}>
              <Tooltip title={action.tooltip}>
                <action.icon color="primary" />
              </Tooltip>
            </Box>
          ))}

        {more && <ContextMenu menuOptions={more.menuItems}></ContextMenu>}
      </Stack>
      {downloadLoading && (
        <Dialog
          open={downloadLoading || false}
          size="sm"
          onClose={() => {}}
          title=""
        >
          <Stack
            minHeight={"200px"}
            justifyContent={"center"}
            direction={"column"}
            alignItems={"center"}
          >
            <CircularProgress size={40} />
            <Typography variant="h3" marginTop={2}>
              Download in progress..
            </Typography>
          </Stack>
        </Dialog>
      )}

      {openDeleteModal && (
        <Dialog
          title=""
          size="sm"
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          actions={[
            {
              type: "button",
              label: "Cancel",
              variant: "outlined",
              onClick: () => setOpenDeleteModal(false),
            },
            {
              type: "submit",
              label: status === "INACTIVE" ? "Reactivate" : "Delete",
              variant: "contained",
              onClick: () => {
                if (status === "INACTIVE") {
                  onReactiveClick?.();
                } else {
                  onDeleteClick?.();
                }
                setOpenDeleteModal(false);
              },
            },
          ]}
        >
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 3,
              }}
            >
              {status === "INACTIVE" ? (
                <RestoreFromTrashIcon
                  sx={{
                    fontSize: 64,
                    color: "success.main",
                    opacity: 0.8,
                  }}
                />
              ) : (
                <WarningAmberIcon
                  sx={{
                    fontSize: 64,
                    color: "error.main",
                    opacity: 0.8,
                  }}
                />
              )}
            </Box>

            <Typography
              variant="h5"
              fontWeight={600}
              color="text.primary"
              sx={{ mb: 2 }}
            >
              {status === "INACTIVE" ? "Reactivate Item" : "Delete Item"}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              {status === "INACTIVE"
                ? "Are you sure you want to reactivate this item? This action will make it active again and available for use."
                : "Are you sure you want to delete this item? This action cannot be undone and will permanently remove the item from the system."}
            </Typography>

            <Alert
              severity={status === "INACTIVE" ? "info" : "warning"}
              sx={{
                textAlign: "left",
                mt: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <AlertTitle>
                {status === "INACTIVE" ? "What happens next?" : "Warning"}
              </AlertTitle>
              {status === "INACTIVE"
                ? "The item will be restored to its previous state and become available for use immediately."
                : "This action is permanent and cannot be reversed. All associated data will be lost."}
            </Alert>
          </Box>
        </Dialog>
      )}
    </>
  );
};
